import { useAuth } from "@clerk/react"
import {
  Copy,
  Download,
  MoreHorizontal,
  Pencil,
  Trash2,
  Upload,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { isRateLimitError } from "../api/rateLimit"
import { ArtworkThumb } from "../cards/ArtworkThumb"
import { FACTION_PALETTE } from "../cards/cardTheme"
import { FACTION_ICON } from "../cards/SkillIcon"
import { useIdbState, useUserIdbState } from "../hooks/useIdbState"
import {
  getAllFactionSearchValues,
  getFactionName,
  getFactionSearchValues,
  getSkillName,
  getSkillSearchValues,
} from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import { isLibrarySyncUnavailable } from "../library/errors"
import {
  LibraryRefreshStatus,
  RecordOriginBadge,
} from "../library/LibrarySyncIndicators"
import { mergeAuthoritativeRemoteRecords } from "../library/merge"
import {
  useBulkDeleteHeroesMutation,
  useDeleteHeroMutation,
  usePullHeroMutation,
  useSyncHeroMutation,
} from "../library/query"
import { fetchManagedHeroRecordsFromServer } from "../library/remote"
import {
  getRemoteSyncState,
  markRecordSyncFailed,
  markRecordSyncPending,
  markRecordSyncSucceeded,
  shouldAutoRetrySync,
} from "../library/syncState"
import { FilterValueButton } from "../manage/FilterValueButton"
import { ManagementFilterBar } from "../manage/ManagementFilterBar"
import type { TableFilterField } from "../manage/tableFilters"
import { useTableFilter } from "../manage/tableFilters"
import { useLibraryTransfer } from "../manage/useLibraryTransfer"
import {
  ARTWORK_ASSET_STORAGE_KEY,
  ARTWORK_ASSET_STORAGE_VERSION,
  ArtworkAssetStoreSchema,
  EMPTY_ARTWORK_ASSET_STORE,
} from "../media/assets"
import { useSessionQuery } from "../query/authenticated"
import { queryKeys } from "../query/keys"
import type { ManagedHeroRecord } from "../schemas"
import { HeroEditorModal } from "./HeroEditorModal"
import {
  buildDuplicatedHeroRecord,
  EMPTY_HERO_LIBRARY,
  HERO_LIBRARY_STORAGE_KEY,
  HERO_LIBRARY_STORAGE_VERSION,
  HeroLibraryStateSchema,
} from "./storage"

function formatRelativeDate(dateString: string, locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(new Date(dateString))
}

function FactionLabel({
  color,
}: {
  color: ManagedHeroRecord["hero"]["primaryColor"]
}) {
  const { t } = useI18n()
  const Icon = FACTION_ICON[color]
  return (
    <span
      className="faction-token"
      style={{ color: FACTION_PALETTE[color].muted }}
    >
      <Icon size={14} strokeWidth={2} />
      <span>{getFactionName(t, color)}</span>
    </span>
  )
}

function HeroProgressionPreview({
  hero,
  onAppendFilter,
}: {
  hero: ManagedHeroRecord["hero"]
  onAppendFilter: (key: string, value: string) => void
}) {
  return (
    <div className="hero-progression-preview">
      {hero.progression.map((level) => (
        <div className="hero-progression-step" key={level.level}>
          <ArtworkThumb
            artwork={level.card.artwork}
            name={`${hero.name} level ${level.level}`}
            primaryColor={hero.primaryColor}
            size="s"
          />
          <span className="hero-progression-stats">
            <FilterValueButton
              filterKey="attack"
              filterValue={String(level.card.stats.attack)}
              onAppendFilter={onAppendFilter}
            >
              {level.card.stats.attack}
            </FilterValueButton>
            /
            <FilterValueButton
              filterKey="health"
              filterValue={String(level.card.stats.health)}
              onAppendFilter={onAppendFilter}
            >
              {level.card.stats.health}
            </FilterValueButton>
          </span>
        </div>
      ))}
    </div>
  )
}

export function HeroLibraryPage() {
  const { locale, t } = useI18n()
  const { userId } = useAuth()
  const [heroes, , setHeroes] = useUserIdbState(
    HERO_LIBRARY_STORAGE_KEY,
    HeroLibraryStateSchema,
    {
      defaultValue: EMPTY_HERO_LIBRARY,
      version: HERO_LIBRARY_STORAGE_VERSION,
    },
  )
  const [artworkAssets, , setArtworkAssets] = useIdbState(
    ARTWORK_ASSET_STORAGE_KEY,
    ArtworkAssetStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_ASSET_STORE,
      version: ARTWORK_ASSET_STORAGE_VERSION,
    },
  )
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<ManagedHeroRecord | null>(
    null,
  )
  const [selectedHeroIds, setSelectedHeroIds] = useState<string[]>([])
  const [pullingHeroIds, setPullingHeroIds] = useState<string[]>([])
  const [remoteRefreshFailed, setRemoteRefreshFailed] = useState(false)
  const [remoteRefreshedAt, setRemoteRefreshedAt] = useState<string | null>(
    null,
  )
  const [syncingHeroIds, setSyncingHeroIds] = useState<string[]>([])
  const heroTransfer = useLibraryTransfer({
    artworkAssets,
    getId: (record) => record.hero.heroId,
    kind: "heroes",
    label: t("table.heroes.title"),
    records: heroes,
    setArtworkAssets,
    setRecords: setHeroes,
  })
  const heroFilterFields = useMemo<
    readonly TableFilterField<ManagedHeroRecord>[]
  >(() => {
    const skillSuggestions = [
      ...[
        ...new Set([
          ...heroes.flatMap((record) =>
            record.hero.progression.flatMap((level) =>
              level.card.selectedSkills.map((selection) => selection.skillId),
            ),
          ),
        ]),
      ],
    ]

    return [
      {
        key: "name",
        getValues: (record) => [record.hero.name],
      },
      {
        key: "id",
        aliases: ["hero"],
        getValues: (record) => [record.hero.heroId],
      },
      {
        key: "slug",
        getValues: (record) => [record.hero.slug],
      },
      {
        key: "faction",
        aliases: ["color"],
        getValues: (record) => [
          ...getFactionSearchValues(t, record.hero.primaryColor),
          ...(record.hero.secondaryColor
            ? getFactionSearchValues(t, record.hero.secondaryColor)
            : []),
        ],
        getSuggestions: () => getAllFactionSearchValues(t),
      },
      {
        key: "attack",
        aliases: ["atk"],
        type: "number",
        getValues: (record) =>
          record.hero.progression.map((level) => level.card.stats.attack),
      },
      {
        key: "health",
        aliases: ["hp"],
        type: "number",
        getValues: (record) =>
          record.hero.progression.map((level) => level.card.stats.health),
      },
      {
        key: "skill",
        aliases: ["skills"],
        getValues: (record) =>
          record.hero.progression.flatMap((level) =>
            level.card.selectedSkills.flatMap((selection) =>
              getSkillSearchValues(t, selection.skillId),
            ),
          ),
        getSuggestions: () => [
          ...skillSuggestions,
          ...skillSuggestions.map((skillId) => getSkillName(t, skillId)),
        ],
      },
      {
        key: "updated",
        getValues: (record) => [
          record.updatedAt,
          formatRelativeDate(record.updatedAt, locale),
        ],
      },
    ]
  }, [heroes, locale, t])

  const heroFilter = useTableFilter({
    records: heroes,
    fields: heroFilterFields,
  })

  const sortedHeroes = useMemo(
    () =>
      [...heroFilter.filteredRecords].sort((a, b) =>
        a.hero.name.localeCompare(b.hero.name),
      ),
    [heroFilter.filteredRecords],
  )
  const hasSelection = selectedHeroIds.length > 0
  const selectedHeroRecords = heroes.filter((record) =>
    selectedHeroIds.includes(record.hero.heroId),
  )

  const syncHeroMutation = useSyncHeroMutation()
  const deleteHeroMutation = useDeleteHeroMutation()
  const pullHeroMutation = usePullHeroMutation()
  const bulkDeleteHeroesMutation = useBulkDeleteHeroesMutation()

  const remoteHeroesQuery = useSessionQuery({
    enabled: Boolean(userId),
    queryFn: ({ sessionToken, signal }) =>
      fetchManagedHeroRecordsFromServer(sessionToken, signal),
    queryKey: queryKeys.library.heroes(userId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!remoteHeroesQuery.data) {
      return
    }

    setHeroes((previous) =>
      mergeAuthoritativeRemoteRecords(
        previous,
        remoteHeroesQuery.data,
        (record) => record.hero.heroId,
      ),
    )
    setRemoteRefreshFailed(false)
    setRemoteRefreshedAt(new Date().toISOString())
  }, [remoteHeroesQuery.data, setHeroes])

  useEffect(() => {
    if (!remoteHeroesQuery.isError) {
      return
    }
    // Stay local-first when the worker authoring sync is not configured.
    setRemoteRefreshFailed(true)
  }, [remoteHeroesQuery.isError])

  const openCreate = () => {
    setEditingRecord(null)
    setEditorOpen(true)
  }

  const openEdit = (record: ManagedHeroRecord) => {
    setEditingRecord(record)
    setEditorOpen(true)
  }

  const handleSave = (saved: ManagedHeroRecord) => {
    setHeroes((prev) => {
      const idx = prev.findIndex((r) => r.hero.heroId === saved.hero.heroId)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = saved
        return next
      }
      return [saved, ...prev]
    })
    setEditorOpen(false)
  }

  const handleDuplicate = (record: ManagedHeroRecord) => {
    setHeroes((prev) => [buildDuplicatedHeroRecord(record, prev), ...prev])
  }

  const handleSyncHero = useCallback(
    (record: ManagedHeroRecord) => {
      if (!userId || syncingHeroIds.includes(record.hero.heroId)) return

      setSyncingHeroIds((previous) => [...previous, record.hero.heroId])
      void syncHeroMutation
        .mutateAsync(record)
        .then((synced) => {
          setHeroes((previous) =>
            previous.map((entry) =>
              entry.hero.heroId === synced.hero.heroId
                ? markRecordSyncSucceeded(synced)
                : entry,
            ),
          )
        })
        .catch((error) => {
          setHeroes((previous) =>
            previous.map((entry) =>
              entry.hero.heroId !== record.hero.heroId
                ? entry
                : isLibrarySyncUnavailable(error) || isRateLimitError(error)
                  ? entry
                  : markRecordSyncFailed(entry),
            ),
          )
        })
        .finally(() => {
          setSyncingHeroIds((previous) =>
            previous.filter((heroId) => heroId !== record.hero.heroId),
          )
        })
    },
    [setHeroes, syncHeroMutation, syncingHeroIds, userId],
  )

  const handlePullRemoteHero = (record: ManagedHeroRecord) => {
    if (!userId || pullingHeroIds.includes(record.hero.heroId)) return

    setPullingHeroIds((previous) => [...previous, record.hero.heroId])
    void pullHeroMutation
      .mutateAsync(record.hero.heroId)
      .then((remoteRecord) => {
        setHeroes((previous) =>
          previous.map((entry) =>
            entry.hero.heroId === remoteRecord.hero.heroId
              ? markRecordSyncSucceeded(remoteRecord)
              : entry,
          ),
        )
      })
      .catch(() => {
        // Leave the local hero in conflict state if remote recovery fails.
      })
      .finally(() => {
        setPullingHeroIds((previous) =>
          previous.filter((heroId) => heroId !== record.hero.heroId),
        )
      })
  }

  useEffect(() => {
    if (!userId) return
    if (typeof navigator !== "undefined" && navigator.onLine === false) return

    const nextRecord = heroes.find(
      (record) =>
        !syncingHeroIds.includes(record.hero.heroId) &&
        shouldAutoRetrySync(record),
    )

    if (!nextRecord) return
    handleSyncHero(markRecordSyncPending(nextRecord))
  }, [handleSyncHero, heroes, syncingHeroIds, userId])

  const handleDelete = (heroId: string) => {
    setHeroes((previous) => {
      const next = previous.filter((r) => r.hero.heroId !== heroId)
      purgeOrphanedArtwork(previous, next)
      return next
    })
    setSelectedHeroIds((prev) => prev.filter((id) => id !== heroId))
    if (!userId) return

    void deleteHeroMutation.mutateAsync(heroId).catch(() => {
      // Keep deletion local-first when the worker path is unavailable.
    })
  }

  const handleBulkDelete = () => {
    const deletedIds = new Set(selectedHeroIds)
    setHeroes((previous) => {
      const next = previous.filter(
        (record) => !deletedIds.has(record.hero.heroId),
      )
      purgeOrphanedArtwork(previous, next)
      return next
    })
    setSelectedHeroIds([])
    if (!userId) return

    void bulkDeleteHeroesMutation.mutateAsync([...deletedIds]).catch(() => {
      // Keep deletion local-first when the worker path is unavailable.
    })
  }

  /** Remove artwork assets no longer referenced by any hero in `next`. */
  const purgeOrphanedArtwork = (
    previous: readonly ManagedHeroRecord[],
    next: readonly ManagedHeroRecord[],
  ) => {
    const remainingIds = new Set(
      next.flatMap((r) =>
        r.hero.progression.flatMap(
          (level) => level.card.artwork?.variants.map((v) => v.assetId) ?? [],
        ),
      ),
    )
    const hadArtwork = previous.some((r) =>
      r.hero.progression.some((level) =>
        level.card.artwork?.variants.some((v) => !remainingIds.has(v.assetId)),
      ),
    )
    if (!hadArtwork) return
    setArtworkAssets((assets) =>
      assets.filter((asset) => remainingIds.has(asset.assetId)),
    )
  }

  return (
    <section className="panel management-panel">
      <HeroEditorModal
        editingRecord={editingRecord}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        open={editorOpen}
      />
      <div className="page-header">
        <div>
          <p className="eyebrow">{t("table.heroes.eyebrow")}</p>
          <h2>{t("table.heroes.title")}</h2>
          <p className="lede table-lede">{t("table.heroes.lede")}</p>
        </div>
        <button className="primary-button" onClick={openCreate} type="button">
          {t("table.heroes.create")}
        </button>
      </div>

      <ManagementFilterBar
        filter={heroFilter}
        placeholder={t("filter.placeholder.heroes")}
      />
      <p className="table-transfer-hint">{t("table.transfer.hint")}</p>

      {heroTransfer.status ? (
        <output
          className={`table-transfer-status table-transfer-status-${heroTransfer.status.tone}`}
        >
          {heroTransfer.status.message}
        </output>
      ) : null}
      <LibraryRefreshStatus
        failed={remoteRefreshFailed}
        lastRefreshedAt={remoteRefreshedAt}
      />

      <section
        aria-label={t("table.heroes.title")}
        aria-busy={heroTransfer.isBusy}
        className={`table-frame${heroTransfer.isDragActive ? " table-frame-drop-active" : ""}`}
        onDragEnter={heroTransfer.onDragEnter}
        onDragLeave={heroTransfer.onDragLeave}
        onDragOver={heroTransfer.onDragOver}
        onDrop={heroTransfer.onDrop}
      >
        {heroTransfer.isDragActive ? (
          <div className="table-drop-zone">
            <strong>
              {t("table.transfer.dropTitle", {
                table: t("table.heroes.title"),
              })}
            </strong>
            <span>{t("table.transfer.dropBody")}</span>
          </div>
        ) : null}
        {hasSelection ? (
          <div className="bulk-bar">
            <button
              aria-label={t("table.bulk.clearSelection")}
              className="bulk-bar-dismiss"
              onClick={() => setSelectedHeroIds([])}
              type="button"
            >
              X
            </button>
            <strong>
              {t("common.selectedCount", { count: selectedHeroIds.length })}
            </strong>
            <div className="bulk-actions">
              <button
                className="secondary-button"
                disabled={
                  heroTransfer.isBusy || selectedHeroRecords.length === 0
                }
                onClick={() =>
                  void heroTransfer.downloadBackup(selectedHeroRecords)
                }
                type="button"
              >
                {t("table.transfer.download")}
              </button>
              <button
                className="danger-button"
                onClick={handleBulkDelete}
                type="button"
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        ) : null}
        <table className="management-table">
          <thead>
            <tr>
              <th className="sticky-column sticky-column-first" scope="col">
                <input
                  aria-label={t("table.heroes.selectAll")}
                  checked={
                    sortedHeroes.length > 0 &&
                    sortedHeroes.every((record) =>
                      selectedHeroIds.includes(record.hero.heroId),
                    )
                  }
                  onChange={() => {
                    setSelectedHeroIds((previous) =>
                      sortedHeroes.every((record) =>
                        previous.includes(record.hero.heroId),
                      )
                        ? previous.filter(
                            (id) =>
                              !sortedHeroes.some(
                                (record) => record.hero.heroId === id,
                              ),
                          )
                        : [
                            ...new Set([
                              ...previous,
                              ...sortedHeroes.map(
                                (record) => record.hero.heroId,
                              ),
                            ]),
                          ],
                    )
                  }}
                  type="checkbox"
                />
              </th>
              <th className="sticky-column sticky-column-name" scope="col">
                {t("table.column.name")}
              </th>
              <th scope="col">{t("table.heroes.factions")}</th>
              <th scope="col">{t("table.heroes.progression")}</th>
              <th scope="col">{t("common.updated")}</th>
              <th className="sticky-column sticky-column-last" scope="col">
                {t("table.column.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedHeroes.map((record) => {
              const isPulling = pullingHeroIds.includes(record.hero.heroId)
              const isSyncing = syncingHeroIds.includes(record.hero.heroId)
              const syncState = getRemoteSyncState(record)
              const canSync = Boolean(userId) && syncState !== "synced"
              const canPullRemote = Boolean(userId) && syncState === "conflict"
              const isSelected = selectedHeroIds.includes(record.hero.heroId)
              return (
                <tr data-selected={isSelected} key={record.hero.heroId}>
                  <td className="sticky-column sticky-column-first">
                    <input
                      aria-label={t("table.row.select", {
                        name: record.hero.name,
                      })}
                      checked={isSelected}
                      onChange={() => {
                        setSelectedHeroIds((previous) =>
                          previous.includes(record.hero.heroId)
                            ? previous.filter((id) => id !== record.hero.heroId)
                            : [...previous, record.hero.heroId],
                        )
                      }}
                      type="checkbox"
                    />
                  </td>
                  <td className="sticky-column sticky-column-name">
                    <div className="identity-cell">
                      <div className="identity-cell-main">
                        <ArtworkThumb
                          artwork={record.hero.progression[0].card.artwork}
                          name={record.hero.name}
                          primaryColor={record.hero.primaryColor}
                          size="m"
                        />
                        <FilterValueButton
                          filterKey="name"
                          filterValue={record.hero.name}
                          onAppendFilter={heroFilter.appendFilterValue}
                        >
                          <strong>{record.hero.name}</strong>
                        </FilterValueButton>
                        <RecordOriginBadge record={record} />
                      </div>
                    </div>
                  </td>
                  <td className="faction-cell">
                    <div className="faction-list">
                      <FilterValueButton
                        filterKey="faction"
                        filterValue={getFactionName(
                          t,
                          record.hero.primaryColor,
                        )}
                        onAppendFilter={heroFilter.appendFilterValue}
                      >
                        <FactionLabel color={record.hero.primaryColor} />
                      </FilterValueButton>
                      {record.hero.secondaryColor ? (
                        <FilterValueButton
                          filterKey="faction"
                          filterValue={getFactionName(
                            t,
                            record.hero.secondaryColor,
                          )}
                          onAppendFilter={heroFilter.appendFilterValue}
                        >
                          <FactionLabel color={record.hero.secondaryColor} />
                        </FilterValueButton>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <HeroProgressionPreview
                      hero={record.hero}
                      onAppendFilter={heroFilter.appendFilterValue}
                    />
                  </td>
                  <td>{formatRelativeDate(record.updatedAt, locale)}</td>
                  <td className="sticky-column sticky-column-last">
                    <div className="row-actions row-actions-desktop">
                      {canSync ? (
                        <button
                          aria-label={t("table.rowAction.sync", {
                            name: record.hero.name,
                          })}
                          className="table-action-button"
                          disabled={isSyncing}
                          onClick={() =>
                            handleSyncHero(markRecordSyncPending(record))
                          }
                          title={t("table.rowAction.sync", {
                            name: record.hero.name,
                          })}
                          type="button"
                        >
                          <Upload size={16} strokeWidth={2} />
                        </button>
                      ) : null}
                      {canPullRemote ? (
                        <button
                          aria-label={t("table.rowAction.pullRemote", {
                            name: record.hero.name,
                          })}
                          className="table-action-button"
                          disabled={isPulling}
                          onClick={() => handlePullRemoteHero(record)}
                          title={t("library.error.syncConflict")}
                          type="button"
                        >
                          <Download size={16} strokeWidth={2} />
                        </button>
                      ) : null}
                      <button
                        aria-label={t("table.rowAction.edit", {
                          name: record.hero.name,
                        })}
                        className="table-action-button"
                        onClick={() => openEdit(record)}
                        title={t("common.edit")}
                        type="button"
                      >
                        <Pencil size={16} strokeWidth={2} />
                      </button>
                      <button
                        aria-label={t("table.rowAction.duplicate", {
                          name: record.hero.name,
                        })}
                        className="table-action-button"
                        onClick={() => handleDuplicate(record)}
                        title={t("common.duplicate")}
                        type="button"
                      >
                        <Copy size={16} strokeWidth={2} />
                      </button>
                      <button
                        aria-label={t("table.rowAction.delete", {
                          name: record.hero.name,
                        })}
                        className="table-action-button table-action-button-danger"
                        onClick={() => handleDelete(record.hero.heroId)}
                        title={t("common.delete")}
                        type="button"
                      >
                        <Trash2 size={16} strokeWidth={2} />
                      </button>
                    </div>
                    <details className="row-actions-menu">
                      <summary
                        aria-label={t("table.row.openActions", {
                          name: record.hero.name,
                        })}
                      >
                        <MoreHorizontal size={16} strokeWidth={2} />
                      </summary>
                      <div className="context-menu">
                        {canSync ? (
                          <button
                            disabled={isSyncing}
                            onClick={() =>
                              handleSyncHero(markRecordSyncPending(record))
                            }
                            type="button"
                          >
                            {t("table.rowAction.sync", {
                              name: record.hero.name,
                            })}
                          </button>
                        ) : null}
                        <button onClick={() => openEdit(record)} type="button">
                          {t("common.edit")}
                        </button>
                        <button
                          onClick={() => handleDuplicate(record)}
                          type="button"
                        >
                          {t("common.duplicate")}
                        </button>
                        <button
                          onClick={() => handleDelete(record.hero.heroId)}
                          type="button"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </details>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </section>
  )
}
